import {
  faBell,
  faCommentSms, faEnvelope, faRepeat,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import { FormikProvider, useFormik } from "formik";
import Link from "next/link";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";

import SharedComponents, { Avatar, ChannelIcon } from "@audacia-hq/shared/components";
import { urlifyPseudo } from "@audacia-hq/shared/utils";

import { useServiceWorker } from "../../context/ServiceWorkerContext";
import { ExpertIdentifier } from "../../models/expert";
import { MemberAlert } from "../../models/member";
import {
  addAlert, editAlert, removeAlert, setPhoneNumberUsage,
} from "../../services/api";
import Button from "../Button";
import PhoneNumberValidationModal from "../members/PhoneNumberValidationModal";
import PushNotificationsModal from "../notifications/PushNotificationsModal";
import PhoneInput from "../PhoneInput";
import { useMember } from "../../context/MemberContext";

interface Props {
  alert?: MemberAlert,
  expertIdentifier: ExpertIdentifier;
  className?: string;
  showExpert?: boolean;
  manualSubmit?: boolean;
  cancelAction?: () => void;
}

const MemberAlertCard: React.FC<Props> = ({
  alert, expertIdentifier, className, manualSubmit = true, showExpert, cancelAction,
}) => {
  const { t } = useTranslation("member");
  const { member, handleMemberGet } = useMember();
  const { loadingPush, pushSupported } = useServiceWorker();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [phoneToValidate, setPhoneToValidate] = useState<string>();
  const [phoneFormLoading, setPhoneFormLoading] = useState<boolean>(false);
  const validityDays = alert?.validTo ? Math.ceil(dayjs(alert.validTo).diff(dayjs(), "day", true)) : 60;

  const deleteAlert = async (alert: MemberAlert) => {
    setLoading(true);
    await removeAlert({
      variables: {
        alertUid: alert.alertUid,
      },
    });
    setLoading(false);
    handleMemberGet();
    toast.success(t("alert.toast.deleteSuccess"));
    if (cancelAction) cancelAction();
  };

  const renewAlert = async (alert: MemberAlert) => {
    setLoading(true);
    await editAlert({
      variables: {
        input: {
          alertUid: alert.alertUid,
          renewValidity: true,
        },
      },
    });
    setLoading(false);
    handleMemberGet();
    toast.success(t("alert.toast.renewSuccess"));
  };

  const handleClose = (error?: string) => {
    setPhoneToValidate(undefined);
    setPhoneFormLoading(false);
    if (error) {
      toast.error(t(`common:errors.${error}`), { autoClose: 5000 });
    }
  };

  const handleValidated = (phoneUid: string) => {
    setPhoneToValidate(undefined);
    setPhoneFormLoading(false);
    setPhoneNumberUsage({
      variables: {
        phoneUid,
        usage: "ALERTS",
      },
    }).then(() => handleMemberGet());
  };

  const formik = useFormik({
    initialValues: {
      PHONE: Array.isArray(alert?.expertChannels) ? alert?.expertChannels?.includes("PHONE") : true,
      VIDEO: Array.isArray(alert?.expertChannels) ? alert?.expertChannels?.includes("VIDEO") : true,
      LIVECHAT: Array.isArray(alert?.expertChannels) ? alert?.expertChannels?.includes("LIVECHAT") : true,
      EMAIL: Array.isArray(alert?.notificationChannels) ? alert?.notificationChannels?.includes("EMAIL") : true,
      SMS: Array.isArray(alert?.notificationChannels) ? alert?.notificationChannels?.includes("SMS") : false,
      PUSH: Array.isArray(alert?.notificationChannels) ? alert?.notificationChannels?.includes("PUSH") : false,
    },
    validationSchema: Yup.object().shape({
      PHONE: Yup.boolean().test({
        name: "oneOfRequired",
        message: t("alert.validation.channelRequired"),
        test: (value, context) => context.parent.PHONE || context.parent.LIVECHAT || context.parent.VIDEO,
      }),
      VIDEO: Yup.boolean(),
      LIVECHAT: Yup.boolean(),
      EMAIL: Yup.boolean().test({
        name: "oneOfRequired",
        message: t("alert.validation.notificationChannelRequired"),
        test: (value, context) => context.parent.EMAIL || context.parent.SMS || context.parent.PUSH,
      }),
      SMS: Yup.boolean(),
      PUSH: Yup.boolean(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (form) => {
      setLoading(true);
      if (alert) {
        const { error } = await editAlert({
          variables: {
            input: {
              alertUid: alert.alertUid,
              expertChannels: [
                ...(form.PHONE ? ["PHONE"] : []),
                ...(form.VIDEO ? ["VIDEO"] : []),
                ...(form.LIVECHAT ? ["LIVECHAT"] : []),
              ],
              notificationChannels: [
                ...(form.EMAIL ? ["EMAIL"] : []),
                ...(form.SMS ? ["SMS"] : []),
                ...(form.PUSH ? ["PUSH"] : []),
              ],
              renewValidity: true,
            },
          },
        });
        if (error) {
          setError(t(error.messages[0]));
        } else {
          handleMemberGet();
          toast.success(t("alert.toast.editSuccess"));
        }
      } else {
        const { error } = await addAlert({
          variables: {
            input: {
              expertUid: expertIdentifier.expertUid,
              expertChannels: [
                ...(form.PHONE ? ["PHONE"] : []),
                ...(form.VIDEO ? ["VIDEO"] : []),
                ...(form.LIVECHAT ? ["LIVECHAT"] : []),
              ],
              notificationChannels: [
                ...(form.EMAIL ? ["EMAIL"] : []),
                ...(form.SMS ? ["SMS"] : []),
                ...(form.PUSH ? ["PUSH"] : []),
              ],
            },
          },
        });
        if (error) {
          setError(t(error.messages[0]));
        } else {
          handleMemberGet();
          toast.success(t("alert.toast.addSuccess"));
        }
      }
      if (cancelAction) {
        cancelAction();
      }
      setLoading(false);
    },
  });

  const phoneFormik = useFormik({
    initialValues: {
      phoneNumber: "",
    },
    validationSchema: Yup.object().shape({
      phoneNumber: Yup.string().required(),
    }),
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (form) => {
      setPhoneFormLoading(true);
      setPhoneToValidate(form.phoneNumber);
    },
  });

  return (
    <div className={`flex flex-col gap-8 p-4 ${className || ""}`}>
      {(formik.values.PUSH && (!loadingPush && pushSupported)) && <PushNotificationsModal onClose={(v) => { formik.setFieldValue("PUSH", v); }} />}
      {expertIdentifier && (
        <FormikProvider value={formik}>

          {showExpert && (
            <div className="flex flex-row items-start gap-3 sm:gap-6">
              <Link href={`/expert-detail/${urlifyPseudo(expertIdentifier.pseudo)}`} className="relative flex-none">
                <Avatar
                  name={expertIdentifier.pseudo}
                  uid={expertIdentifier.expertUid}
                  src={expertIdentifier.picture}
                  loading="lazy"
                  className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg"
                />
              </Link>
              <div className="md:w-72">
                <Link href={`/expert-detail/${urlifyPseudo(expertIdentifier.pseudo)}`} className="flex flex-col gap-3 sm:gap-6 mb-2">
                  <div className="flex flex-wrap items-start gap-x-3">
                    <div className="font-semibold text-lg sm:text-xl">{expertIdentifier.pseudo}</div>
                  </div>
                </Link>
                <SharedComponents.Tooltip
                  position="bottom"
                  className="w-48"
                  arrow
                  message={t("common:accessCodeTips")}
                >
                  <span className="border border-slate-100 bg-slate-50 hover:bg-slate-100 text-gray-800 text-sm font-medium px-1 py-0.5 rounded">{String(expertIdentifier.expertCode).padStart(3, "0")}</span>
                </SharedComponents.Tooltip>
              </div>
            </div>
          )}

          {(error) && (
            <div className="text-red-500">{error}</div>
          )}

          <div>
            <span className="mb-2 block font-semibold">{t("alert.notificationChannel")}</span>
            <div className="flex flex-row flex-nowrap gap-6 ">
              {["EMAIL", "SMS", "PUSH"]
                .map((c, idx) => (
                  <div
                    className={(c === "PUSH" && (loadingPush || !pushSupported)) ? "hidden" : "form-check form-check-inline flex gap-1"}
                    key={idx}
                  >
                    <input
                      id={`notif-channel-${c}`}
                      className={`rounded-full h-4 w-4 border border-gray-300 bg-white 
                        focus:outline-hidden transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain 
                        float-left mr-2 cursor-pointer`}
                      type="checkbox"
                      name={c}
                      onChange={() => {
                        formik.setFieldValue(c, !formik.values[c]).then(() => { if (!manualSubmit) formik.submitForm(); });
                      }}
                      checked={formik.values[c] || false}
                    />
                    <label className="form-check-label inline-block text-gray-800" htmlFor={`notif-channel-${c}`}>
                      {c === "EMAIL" && (<FontAwesomeIcon icon={faEnvelope} />)}
                      {c === "SMS" && (<FontAwesomeIcon icon={faCommentSms} />)}
                      {c === "PUSH" && (<FontAwesomeIcon icon={faBell} />)}
                      {" "}
                      {t(`common:notificationChannels.${c}`)}
                    </label>
                  </div>
                ))}
            </div>
            {(formik.errors.EMAIL) && (
              <span className="text-red-500">{formik.errors.EMAIL}</span>
            )}
          </div>

          {formik.values.SMS && member && !member.profile?.phoneUidForAlerts && (

            <FormikProvider value={phoneFormik}>
              <div>
                {t("alert.smsRequirements")}
                <div>
                  <PhoneInput
                    name="phoneNumber"
                    selectedCountry={member?.country}
                    acceptedPhoneTypes={["MOBILE"]}
                    dropdownClassName="h-28"
                    inlineSubmitAction={() => phoneFormik.submitForm()}
                    inlineSubmitLabel={t("common:validate")}
                    hideErrors
                    loading={phoneFormLoading}
                  />
                </div>
                {phoneFormik.errors.phoneNumber && (
                  <div className="text-red-500">
                    {phoneFormik.errors.phoneNumber}
                  </div>
                )}
              </div>

            </FormikProvider>
          )}

          {phoneToValidate && (
            <PhoneNumberValidationModal
              phoneNumber={phoneToValidate}
              onClose={handleClose}
              onValidated={handleValidated}
            />
          )}

          <div>
            <span className="mb-2 block font-semibold">{t("alert.channels")}</span>
            <div className="flex flex-row flex-nowrap gap-6 ">
              {["PHONE", "VIDEO", "LIVECHAT"]
                .map((c, idx) => (
                  <div className="form-check form-check-inline flex gap-1" key={idx}>
                    <input
                      id={`channel-${c}`}
                      className={`rounded-full h-4 w-4 border
                      border-gray-300 bg-white 
                        focus:outline-hidden transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain 
                        float-left mr-2 cursor-pointer`}
                      type="checkbox"
                      name={c}
                      onChange={() => {
                        formik.setFieldValue(c, !formik.values[c]).then(() => { if (!manualSubmit) formik.submitForm(); });
                      }}
                      checked={formik.values[c] || false}
                    />
                    <label className="form-check-label inline-block text-gray-800" htmlFor={`channel-${c}`}>
                      <ChannelIcon key={c} channel={c as any} />
                      {" "}
                      {t(`common:channels.${c}`)}
                    </label>
                  </div>
                ))}
            </div>
            {(formik.errors.PHONE) && (
              <span className="text-red-500">{formik.errors.PHONE}</span>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <span className="font-semibold">{t("alert.validity")}</span>
            <span>{t("alert.validityDays", { nbDays: validityDays })}</span>
            {alert && validityDays < 60 && (
              <Button render="simple" size="medium" onClick={() => renewAlert(alert)}>
                <FontAwesomeIcon icon={faRepeat} />
                {" "}
                {t("common:renew")}
              </Button>
            )}
          </div>

          {manualSubmit ? (
            <div className="flex flex-row-reverse gap-3 mt-4">
              <Button
                onClick={() => formik.submitForm()}
                // className={formik.values.SMS && member && !member.profile?.phoneUidForAlerts ? "!bg-blue-100" : ""}
                disabled={loading || (formik.values.SMS && member && !member.profile?.phoneUidForAlerts)}
                loading={loading}
                size="medium"
              >
                {t("common:save")}
              </Button>
              {alert && (<Button disabled={loading} loading={loading} onClick={() => deleteAlert(alert)} size="medium" render="simpleRed">{t("common:delete")}</Button>)}
              {cancelAction && (<Button disabled={loading} loading={loading} onClick={() => cancelAction()} size="medium" render="transparent">{t("common:cancel")}</Button>)}
            </div>
          ) : (
            <div className="flex flex-row-reverse">
              {alert && (<Button disabled={loading} loading={loading} onClick={() => deleteAlert(alert)} size="small" render="simpleRed">{t("common:delete")}</Button>)}
            </div>
          )}

        </FormikProvider>
      )}
    </div>
  );
};

export default MemberAlertCard;
